import { createContext, useContext, useMemo, type ReactNode } from "react";
import { usePermissions } from "../../session/permissions";
import { useWorkspaceId } from "../../session/workspace-context";
import type { ServiceAreaId } from "./areas";
import { serviceKeys } from "./service-keys";
import { useServiceQuery } from "./use-service-query";

/**
 * The Service modules a workspace can switch on or off (scope TKT-203). Tickets are always on; the rest are plan-gated
 * and toggled in Service settings. `orders` covers the catalogue too.
 */
export const SERVICE_MODULE_IDS = ["tickets", "organisations", "assets", "installations", "visits", "orders"] as const;
export type ServiceModuleId = (typeof SERVICE_MODULE_IDS)[number];

/** The server names the installations module `projects`; every other module has the same key on both sides. */
const MODULE_ID_BY_API_KEY: Readonly<Record<string, ServiceModuleId>> = {
  tickets: "tickets",
  organisations: "organisations",
  assets: "assets",
  projects: "installations",
  visits: "visits",
  orders: "orders",
};

/** The API key of a console module id (`installations` is `projects` on the server). */
export function apiModuleKey(module: ServiceModuleId): string {
  return module === "installations" ? "projects" : module;
}

/** The console module ids the server reports as active (switched on, and organisations only outside B2C mode). */
export function activeModuleIds(modules: readonly { key: string; active: boolean }[]): ServiceModuleId[] {
  return modules.flatMap((module) => {
    const id = MODULE_ID_BY_API_KEY[module.key];
    return module.active && id !== undefined ? [id] : [];
  });
}

/** The module each Service page belongs to. Contacts, reports and settings are part of Tickets (B2C has contacts, no organisations). */
export const SERVICE_AREA_MODULE: Readonly<Record<ServiceAreaId, ServiceModuleId>> = {
  tickets: "tickets",
  organisations: "organisations",
  contacts: "tickets",
  assets: "assets",
  installations: "installations",
  visits: "visits",
  orders: "orders",
  catalogue: "orders",
  "service-reports": "tickets",
  "service-settings": "tickets",
};

/** What every new workspace has on (mirrors the server's default): what the screens assume until the switches have been read. */
const DEFAULT_ON: readonly ServiceModuleId[] = ["tickets", "organisations"];

export interface ServiceModules {
  isEnabled: (module: ServiceModuleId) => boolean;
  /** The workspace's module switches are still loading: a page of an optional module waits instead of flashing. */
  isPending: boolean;
}

/** Without the workspace's switches (a member who cannot read them, an older server) nothing is hidden here; the server's nav and routes still decide. */
const ALL_ENABLED: ServiceModules = { isEnabled: () => true, isPending: false };

/** Until the switches are read only the default modules exist: an optional module's entry points appear once the server says they are on, never flash. */
const PENDING: ServiceModules = { isEnabled: (module) => DEFAULT_ON.includes(module), isPending: true };

/** A fixed set of modules that replaces the server's answer below it (a preview or a test that has no workspace behind it). */
const ServiceModulesContext = createContext<ServiceModules | null>(null);

export function ServiceModulesProvider({ enabled, children }: { enabled: readonly ServiceModuleId[]; children: ReactNode }) {
  const value = useMemo<ServiceModules>(() => ({ isEnabled: (module) => module === "tickets" || enabled.includes(module), isPending: false }), [enabled]);
  return <ServiceModulesContext.Provider value={value}>{children}</ServiceModulesContext.Provider>;
}

/**
 * The workspace's module switches, read from `GET /v1/service-settings` (`modules[].active`: switched on, and no organisations
 * in B2C mode) through the shared query cache, so every guard and entry point of the console - a Service page, a settings tab,
 * an organisation tab, a ticket's context panel, a dashboard card - answers from the same data wherever it sits in the tree and
 * a module that is off is a 404 page with no entry points. (The answer deliberately does not travel through a React context
 * that a provider would have to wrap each page in: a surface outside the wrapper would silently show everything.)
 * Needs `tickets.view`; a member without it, or a failed read, is not filtered here (the server still answers 404).
 */
export function useServiceModules(): ServiceModules {
  const fixed = useContext(ServiceModulesContext);
  const ws = useWorkspaceId();
  const permissions = usePermissions();
  const permitted = permissions.can("tickets.view");
  const settings = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "general"),
    enabled: fixed === null && permitted,
    staleTime: 30_000,
    request: (api, signal) => api.GET("/v1/service-settings", { signal }),
  });
  const data = settings.data;
  // A read that failed stays "failed" while another observer retries it (a refetch of an errored query reports pending again), so a page is not torn down and rebuilt in a loop.
  const failed = data === undefined && settings.errorUpdatedAt > 0;
  return useMemo<ServiceModules>(() => {
    if (fixed !== null) return fixed;
    if (permissions.isPending) return PENDING;
    if (!permitted || failed) return ALL_ENABLED;
    if (data === undefined) return PENDING;
    const on = activeModuleIds(data.modules);
    return { isEnabled: (module) => module === "tickets" || on.includes(module), isPending: false };
  }, [fixed, permissions.isPending, permitted, failed, data]);
}

export function useServiceModuleEnabled(module: ServiceModuleId): boolean {
  return useServiceModules().isEnabled(module);
}

/** The module switches are still being read: pages wait for them (a skeleton, not a flash of the wrong page). */
export function useServiceModulesPending(): boolean {
  return useServiceModules().isPending;
}
